import React, { useState } from 'react';
import { galleryImages, galleryVideos } from '../data/galleryImages';
import { Play } from 'lucide-react';

const GaleriaPage = () => {
  const [activeCategory, setActiveCategory] = useState('todos');

  const categories = [
    { id: 'todos', name: 'Todos' },
    { id: 'ramos', name: 'Ramos' },
    { id: 'arreglos', name: 'Arreglos' },
    { id: 'boxes', name: 'Boxes' },
    { id: 'peluches', name: 'Peluches' },
    { id: 'eventos', name: 'Eventos' },
    { id: 'detalles', name: 'Detalles' }
  ];

  const currentImages = galleryImages[activeCategory] || [];

  return (
    <div className="min-h-screen bg-pink-50 pt-16 md:pt-20">
      {/* Hero Section */}
      <section 
        className="py-12 md:py-16 px-4"
        style={{
          backgroundImage: 'url(https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A/e9bba638-d807-427f-bea3-a775809cad00/publicContain)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-pink-900 mb-4 md:mb-6">
            Nuestra Galería
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-pink-800">
            Descubre la belleza de nuestros arreglos florales
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-6 md:py-8 px-4 bg-white sticky top-16 md:top-20 z-40 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-pink-600 text-white shadow-lg scale-105'
                    : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {currentImages.map((image, index) => (
              <div 
                key={index}
                className="group overflow-hidden bg-white rounded-xl md:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer aspect-square"
              >
                <img 
                  src={image}
                  alt={`Arreglo floral ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-pink-900 mb-8 md:mb-12">
            Videos de Nuestros Trabajos
          </h2>
          <p className="text-center text-pink-700 text-lg md:text-xl mb-8 md:mb-12">
            Mira cómo creamos cada detalle con dedicación y amor
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {galleryVideos.map((video) => (
              <a
                key={video.id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer aspect-[4/5]"
              >
                <img 
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-white/90 rounded-full p-4 md:p-6 transform group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 md:w-12 md:h-12 text-pink-600 fill-current" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-white font-semibold text-base md:text-lg">{video.title}</p>
                </div>
              </a>
            ))}
          </div>

          {/* Social Media Links */}
          <div className="text-center">
            <p className="text-pink-800 text-lg md:text-xl mb-6">Síguenos en nuestras redes sociales</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://www.instagram.com/tqm.floreria"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 md:px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Instagram
              </a>
              <a
                href="https://www.facebook.com/tqmflores"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 md:px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Facebook
              </a>
              <a
                href="https://www.tiktok.com/@tqmfloreria"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black text-white px-6 md:px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                TikTok
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 px-4 bg-pink-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-pink-900 mb-4 md:mb-6">
            ¿Te gustó lo que viste?
          </h2>
          <p className="text-base md:text-lg text-pink-800 mb-6 md:mb-8">
            Haz tu pedido ahora y recibe flores frescas en menos de 3 horas
          </p>
          <button
            onClick={() => window.open('https://wa.me/51959389799?text=Hola,%20vi%20la%20galería%20y%20me%20gustaría%20hacer%20un%20pedido', '_blank')}
            className="bg-green-500 hover:bg-green-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Hacer mi Pedido
          </button>
        </div>
      </section>
    </div>
  );
};

export default GaleriaPage;